import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

// Carrusel 3D tipo coverflow, en bucle, con giro automático, swipe, flechas y puntos.
const Flyers360 = ({ block, SectionHeading }) => {
  const items = (block.items || []).filter((it) => it.image).slice(0, 8);
  const n = items.length;
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(null);
  const touch = useRef(null);
  const boxRef = useRef(null);
  const [box, setBox] = useState({ w: 360 });
  const horiz = block.orientation === "horizontal";
  const [ratios, setRatios] = useState({});

  useEffect(() => {
    if (!boxRef.current) return;
    const ro = new ResizeObserver(([e]) => setBox({ w: e.contentRect.width }));
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, [n]);

  // Tamaño real de cada flayer (sin recortes). Vertical: tarjetas altas; horizontal: anchas.
  const maxH = horiz ? Math.min(box.w * 0.78, 420) : Math.min(box.w * 1.18, 560);
  const fit = (id, limitH) => {
    const r = ratios[id] || (horiz ? 1.5 : 0.8);
    let h = limitH;
    let w = h * r;
    const maxW = box.w * (horiz ? 0.84 : 0.76);
    if (w > maxW) {
      w = maxW;
      h = w / r;
    }
    return { w, h };
  };
  const activeId = items[active]?.id;
  const activeSize = fit(activeId, maxH);
  // Las tarjetas de los lados nunca son más altas que la del centro.
  const cardSize = (id) => (id === activeId ? activeSize : fit(id, activeSize.h));

  const paused = useRef(false);

  const go = (d) => setActive((a) => (a + d + n) % n);

  useEffect(() => {
    if (n < 2) return;
    const t = setInterval(() => {
      if (!paused.current) setActive((a) => (a + 1) % n);
    }, 3200);
    return () => clearInterval(t);
  }, [n]);

  const pause = () => {
    paused.current = true;
    clearTimeout(pause.t);
    pause.t = setTimeout(() => (paused.current = false), 6000);
  };

  const onStart = (e) => {
    touch.current = e.touches[0].clientX;
    pause();
  };
  const onEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (Math.abs(dx) > 35) go(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  const title = block.title && <SectionHeading titleSize={block.titleSize}>{block.title}</SectionHeading>;

  if (!n) {
    return (
      <div className="w-full">
        {title}
        <p className="mt-4 text-center text-xs text-white/40">Agrega flayers para ver el carrusel 360.</p>
      </div>
    );
  }

  const offset = (i) => {
    let d = i - active;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  };

  return (
    <div className="w-full" data-testid="flyers360">
      {title}
      <div
        ref={boxRef}
        className="relative mt-4 select-none"
        style={{ perspective: 1100, height: activeSize.h + 8, transition: "height .45s cubic-bezier(.22,.8,.3,1)" }}
        onTouchStart={onStart}
        onTouchEnd={onEnd}
      >
        {items.map((it, i) => {
          const d = offset(i);
          const ad = Math.abs(d);
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => (d === 0 ? setZoom(it.image) : (pause(), setActive(i)))}
              className="absolute left-1/2 top-1/2 overflow-hidden rounded-2xl border border-white/15 bg-black/30 shadow-[0_18px_40px_rgba(0,0,0,.55)]"
              style={{
                width: cardSize(it.id).w,
                height: cardSize(it.id).h,
                transform: `translate(-50%,-50%) translateX(${d * box.w * 0.34}px) translateZ(${-ad * 140}px) rotateY(${-d * 38}deg)`,
                zIndex: 20 - ad,
                opacity: ad > 2 ? 0 : ad === 2 ? 0.45 : 1,
                pointerEvents: ad > 2 ? "none" : "auto",
                transition: "transform .6s cubic-bezier(.22,.8,.3,1), opacity .6s, width .3s, height .3s",
              }}
              data-testid={`flyer-${i}`}
            >
              <img
                src={it.image}
                alt=""
                loading="eager"
                draggable={false}
                className="h-full w-full object-contain"
                onLoad={(e) => {
                  const { naturalWidth: nw, naturalHeight: nh } = e.currentTarget;
                  if (nw && nh) setRatios((r) => (r[it.id] ? r : { ...r, [it.id]: nw / nh }));
                }}
              />
              {d !== 0 && <span className="absolute inset-0 bg-black/35" />}
            </button>
          );
        })}
        {n > 1 && (
          <>
            <button
              onClick={() => (pause(), go(-1))}
              className="absolute left-1 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/65"
              data-testid="flyers-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => (pause(), go(1))}
              className="absolute right-1 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/65"
              data-testid="flyers-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
      {items.some((x) => x.name) && (
        <p
          key={active}
          className="mt-2 animate-in fade-in slide-in-from-bottom-1 px-4 text-center font-display font-semibold leading-tight text-white duration-500"
          style={{ fontSize: block.nameSize || 18, minHeight: (block.nameSize || 18) * 1.25 }}
          data-testid="flyer-name"
        >
          {items[active]?.name || ""}
        </p>
      )}
      {n > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {items.map((it, i) => (
            <button
              key={it.id}
              onClick={() => (pause(), setActive(i))}
              className={`h-2 rounded-full transition-all duration-300 ${i === active ? "w-6 bg-white" : "w-2 bg-white/35"}`}
              aria-label={`Flayer ${i + 1}`}
            />
          ))}
        </div>
      )}
      {(() => {
        const cur = items[active];
        const num = (cur?.wa || "").replace(/\D/g, "");
        const anyWa = items.some((x) => (x.wa || "").replace(/\D/g, ""));
        if (!anyWa) return null;
        if (!num) return <div className="mt-4 h-11" />;
        const msg = cur.waMsg || (cur.name ? `Hola, quiero información de: ${cur.name}` : "Hola, quiero información de esta promoción.");
        return (
          <div className="mt-4 flex h-11 justify-center">
            <a
              href={`https://wa.me/${num}?text=${encodeURIComponent(msg)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={pause}
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white shadow-[0_10px_25px_rgba(37,211,102,.35)] transition hover:scale-105 active:scale-95"
              data-testid="flyer-whatsapp-btn"
            >
              <FaWhatsapp className="h-5 w-5" /> Pedir información
            </a>
          </div>
        );
      })()}
      {zoom && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setZoom(null)}
          data-testid="flyer-zoom"
        >
          <button className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white">
            <X className="h-5 w-5" />
          </button>
          <img src={zoom} alt="" className="max-h-full max-w-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
};

export default Flyers360;
